import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon } from 'lucide-react';
import type { QuestionRendererProps } from './types';
import { FALLBACK_COLOR } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function CoordinatePlotRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const data = question.data || {};
  const gridWidth = Number(data.gridWidth ?? 10);
  const gridHeight = Number(data.gridHeight ?? 10);
  const startPoint = data.startPoint as {
    x: number;
    y: number;
    label: string;
  } | undefined;
  const [pickedPoint, setPickedPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);
  useEffect(() => {
    setPickedPoint(null);
  }, [question.id]);
  const isReady = pickedPoint !== null;
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  const answerPayload = useMemo(() => {
    return pickedPoint ? JSON.stringify(pickedPoint) : '';
  }, [pickedPoint]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const cellSize = 28;
  const padding = 50;
  const arrowSize = 8;
  const svgWidth = gridWidth * cellSize + padding * 2;
  const svgHeight = gridHeight * cellSize + padding * 2;
  const originX = padding;
  const originY = svgHeight - padding;
  const toSvgX = (x: number) => originX + x * cellSize;
  const toSvgY = (y: number) => originY - y * cellSize;
  const handleGridClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (shouldValidate) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Convert SVG coordinates back to grid coordinates
    const gridX = Math.round((x - originX) / cellSize);
    const gridY = Math.round((originY - y) / cellSize);
    if (gridX >= 0 && gridX <= gridWidth && gridY >= 0 && gridY <= gridHeight) {
      setPickedPoint({
        x: gridX,
        y: gridY
      });
    }
  };
  return <div className="flex flex-col items-center gap-3 w-full" data-id="element-11461" data-mp="1x0zpzbt8qg66">
      <div className="text-sm text-stone-500 flex items-center gap-1.5" data-id="element-11462" data-mp="1s02a55vpdy7u">
        <MapPinIcon className="w-4 h-4" data-id="element-11463" data-mp="1s2eqjeltc0is" />
        Klik op het assenstelsel om je punt te plaatsen
      </div>

      <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 touch-none" data-id="element-11464" data-mp="1izx2qvqiezto">
        <div className="flex justify-center" data-id="element-11465" data-mp="1j1qcwl0c026f">
          <motion.svg width={svgWidth} height={svgHeight} role="img" aria-label={`Interactief coördinatenstelsel ${gridWidth} bij ${gridHeight}`} onClick={handleGridClick} className={shouldValidate ? 'cursor-default' : 'cursor-pointer'} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3
        }} data-id="element-11466" data-mp="1e85ljbt8u6g7">
            {/* Grid lines */}
            <g className="grid-lines" data-id="element-11467" data-mp="3700rte5xjkt2">
              {Array.from({
              length: gridWidth + 1
            }).map((_, i) => <line key={`v-${i}`} x1={toSvgX(i)} y1={toSvgY(0)} x2={toSvgX(i)} y2={toSvgY(gridHeight)} stroke="#e2e8f0" strokeWidth="1" data-id="element-11468" data-mp="3fy2yoacch7o9" data-mpi={i} />)}
              {Array.from({
              length: gridHeight + 1
            }).map((_, i) => <line key={`h-${i}`} x1={toSvgX(0)} y1={toSvgY(i)} x2={toSvgX(gridWidth)} y2={toSvgY(i)} stroke="#e2e8f0" strokeWidth="1" data-id="element-11469" data-mp="2x6trw8tykx0d" data-mpi={i} />)}
            </g>

            {/* X-axis */}
            <g className="x-axis" data-id="element-11470" data-mp="29g7jggg1lapu">
              <line x1={originX - 10} y1={originY} x2={toSvgX(gridWidth) + 15} y2={originY} stroke="#475569" strokeWidth="2" data-id="element-11471" data-mp="f1k8jvikbgw1" />
              <polygon points={`${toSvgX(gridWidth) + 15},${originY} ${toSvgX(gridWidth) + 15 - arrowSize},${originY - arrowSize / 2} ${toSvgX(gridWidth) + 15 - arrowSize},${originY + arrowSize / 2}`} fill="#475569" data-id="element-11472" data-mp="osn64nca9dqk" />
              <text x={toSvgX(gridWidth) + 20} y={originY + 5} className="text-sm font-bold fill-slate-600" data-id="element-11473" data-mp="trhpbqe5x4b7">
                x
              </text>
            </g>

            {/* Y-axis */}
            <g className="y-axis" data-id="element-11474" data-mp="12sfz6pba861p">
              <line x1={originX} y1={originY + 10} x2={originX} y2={toSvgY(gridHeight) - 15} stroke="#475569" strokeWidth="2" data-id="element-11475" data-mp="39hugjgg67f09" />
              <polygon points={`${originX},${toSvgY(gridHeight) - 15} ${originX - arrowSize / 2},${toSvgY(gridHeight) - 15 + arrowSize} ${originX + arrowSize / 2},${toSvgY(gridHeight) - 15 + arrowSize}`} fill="#475569" data-id="element-11476" data-mp="1k29zrkx4u6zn" />
              <text x={originX - 5} y={toSvgY(gridHeight) - 20} textAnchor="middle" className="text-sm font-bold fill-slate-600" data-id="element-11477" data-mp="1f9qncra6lhwf">
                y
              </text>
            </g>

            {/* X-axis numbers */}
            {Array.from({
            length: gridWidth + 1
          }).map((_, i) => <text key={`x-num-${i}`} x={toSvgX(i)} y={originY + 20} textAnchor="middle" className="text-xs fill-slate-500" data-id="element-11478" data-mp="wb4pdl9yo6wq" data-mpi={i}>
                {i}
              </text>)}

            {/* Y-axis numbers */}
            {Array.from({
            length: gridHeight + 1
          }).map((_, i) => <text key={`y-num-${i}`} x={originX - 15} y={toSvgY(i) + 4} textAnchor="middle" className="text-xs fill-slate-500" data-id="element-11479" data-mp="3umrcwlgk9sbg" data-mpi={i}>
                {i}
              </text>)}

            {/* Start Point */}
            {startPoint && <g data-id="element-11480" data-mp="m6o76l90edpr">
                <circle cx={toSvgX(startPoint.x)} cy={toSvgY(startPoint.y)} r={8} fill="#94a3b8" stroke="white" strokeWidth="2" data-id="element-11481" data-mp="m9afin3q4x91" />
                <text x={toSvgX(startPoint.x) + 12} y={toSvgY(startPoint.y) - 8} className="text-sm font-bold fill-slate-500" data-id="element-11482" data-mp="8eztidj43h6o">
                  {startPoint.label}
                </text>
              </g>}

            {/* Picked Point */}
            {pickedPoint && <motion.g initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }} data-id="element-11483" data-mp="1dey5i2oe6m66">
                <circle cx={toSvgX(pickedPoint.x)} cy={toSvgY(pickedPoint.y)} r={10} fill={color} stroke="white" strokeWidth="2" data-id="element-11484" data-mp="1rdpghg7yjrq3" />
                <text x={toSvgX(pickedPoint.x) + 14} y={toSvgY(pickedPoint.y) + 4} className="text-sm font-bold" style={{
              fill: color
            }} data-id="element-11485" data-mp="216wkawn1uzo2">
                  ({pickedPoint.x}, {pickedPoint.y})
                </text>
              </motion.g>}

            {/* Invisible hit targets for mobile */}
            {!shouldValidate && Array.from({
            length: gridWidth + 1
          }).map((_, x) => Array.from({
            length: gridHeight + 1
          }).map((_, y) => <circle key={`hit-${x}-${y}`} cx={toSvgX(x)} cy={toSvgY(y)} r={14} fill="transparent" className="cursor-pointer" onClick={e => {
            e.stopPropagation();
            setPickedPoint({
              x,
              y
            });
          }} data-id="element-11486" data-mp="35f4ls7r4ozjz" data-mpi={y} />))}
          </motion.svg>
        </div>
      </div>

      <p className="text-sm text-stone-500 flex items-center gap-1.5 min-h-[1.25rem]" aria-live="polite" data-id="element-11487" data-mp="1ty4wzm3zp2up">
        {pickedPoint ? <>
            <span className="inline-block w-2 h-2 rounded-full" style={{
          backgroundColor: color
        }} aria-hidden="true" data-id="element-11488" data-mp="e7lw7grhyc82" />
            Punt geplaatst op ({pickedPoint.x}, {pickedPoint.y})
          </> : <span className="text-stone-400" data-id="element-11489" data-mp="s7wmahkzfms6">
            Kies een punt op het assenstelsel
          </span>}
      </p>
    </div>;
}